<template>
  <page class="leaderboard-show" :loading="!leaderboard">
    <b-row class="top-header mb-3" v-if="leaderboard">
      <b-col sm="9" lg="10">
        <h2>{{ leaderboard.name || 'Leaderboard' }}</h2>
        <p v-if="leaderboard.description">{{ leaderboard.description }}</p>
      </b-col>
      <b-col class="text-right" sm="3" lg="2">
        <span class="match-label bg-primary-a1 text-primary" v-if="leaderboard.endDate">
          <template v-if="leaderboard.endDate < Date.now()">ENDED ON</template>
          <template v-else>ENDS ON</template>
          {{ leaderboard.endDate | formatDate('DD/MMM') }}
        </span>
        <b-dropdown id="more-leaderboard" right size="sm" text="More" variant="primary" v-if="isRecruiterAdmin">
          <b-dropdown-item @click="stopAutoUpdates" v-if="updateInterval">Disable Auto-Updates</b-dropdown-item>
          <b-dropdown-item @click="startAutoUpdates" v-else>Enable Auto-Updates</b-dropdown-item>
          <b-dropdown-item v-if="raffleMode" v-b-modal="`raffle-spinner-${leaderboard.id}`">Draw Winner</b-dropdown-item>
        </b-dropdown>
      </b-col>
    </b-row>

    <b-row v-if="leaderboard" class="mb-3">
      <b-col class="mb-3" lg="8" v-if="leaderboard.prizes">
        <LeaderboardPrizes
          :first="leaderboard.prizes.first" :second="leaderboard.prizes.second" :third="leaderboard.prizes.third"
          :text="leaderboard.prizes.text" :isRaffle="raffleMode" :scores="prizesData"/>
      </b-col>
      <b-col lg="4">
        <b-card class="points-system" v-if="leaderboard.scores">
          <h2>{{ $t(raffleMode ? 'leaderboards.scores.how-to-earn-tickets' : 'leaderboards.scores.how-to-earn-points') }}</h2>
          <template v-for="(score, key) in leaderboard.scores">
            <b-row class="point-type" :key="`${key}-${score.id}`" v-if="!score.disabled">
              <b-col cols="2" class="icon-container">
                <div class="bg-primary-a2 round-icon" v-if="score.icon"><font-awesome-icon :icon="score.icon" /></div>
              </b-col>
              <b-col cols="10" class="d-flex flex-column justify-content-center">
                <span class="header" v-if="raffleMode">{{ $t('leaderboards.scores.tickets-for-action', { pointsTotal: score.value, actionName: score.label }) }}</span>
                <span class="header" v-else>{{ $t('leaderboards.scores.points-for-action', { pointsTotal: score.value, actionName: score.label }) }}</span>
                <span v-if="score.description">{{ score.description }}</span>
              </b-col>
            </b-row>
          </template>
        </b-card>
      </b-col>
    </b-row>

    <b-card id="filter-users-container" no-body>
      <leaderboard-table :leaderboard="leaderboard" :animate="Boolean(updateInterval)" :limit="updateInterval ? 50 : 20"/>
    </b-card>

    <raffle-spinner-modal v-if="leaderboard && isRecruiterAdmin && raffleMode" :leaderboardID="leaderboard.id" />

  </page>
</template>

<script>
import ms from 'ms';
import LeaderboardShowQuery from '@/graphql/queries/LeaderboardShow.gql';
import LeaderboardPrizes from '@/components/leaderboards/Prizes';
import LeaderboardTable from '@/components/leaderboards/Table';
import Page from '@/components/Page';
import PercentageChange from '@/components/dashboard/percentage-change';
import RaffleSpinnerModal from '@/modals/raffle-spinner';
import timeDistance from 'date-fns/distance_in_words_to_now';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    leaderboardID: String,
  },
  components: {
    Page,
    PercentageChange,
    RaffleSpinnerModal,
    LeaderboardPrizes,
    LeaderboardTable,
  },
  data: () => ({
    err: null,
    type: 'INDIVIDUAL', // type of leaderboard
    state: 'INDIVIDUAL', // state of Leaderboard as Individual Leaderboard may toggle beteen departments
    historyFrom: null,
    historyLatest: null,
    updateInterval: null,
  }),
  apollo: {
    leaderboard: {
      query: LeaderboardShowQuery,
      variables() {
        const { leaderboardID, historyFrom, updateInterval } = this;
        return {
          leaderboardID,
          withHistory: Boolean(updateInterval),
          historyFrom,
        };
      },
      update({ company }) {
        const { leaderboard } = company || {};
        return leaderboard;
      },
      result({ loading, data }) {
        if (!loading && data && this.historyFrom) {
          this.notifyLatestHistory();
        }
      },
      error(err) {
        if (this.leaderboard) {
          this.$log.error(err);
        } else {
          this.$notifyErr(err);
        }
      },
      fetchPolicy: 'network-only',
    },
  },
  metaInfo() {
    const meta = {};

    if (this.leaderboard && this.leaderboard.name) {
      meta.title = `${this.leaderboard.name} - Leaderboard`;
    } else {
      meta.title = 'Leaderboard';
    }

    return meta;
  },
  computed: {
    ...mapRoot([ 'currentUserID', 'isRecruiterAdmin' ]),
    isDepartmentLeaderboard() {
      return this.leaderboard && this.leaderboard.type === 'DEPARTMENTS';
    },
    prizesData() {
      if (this.leaderboard) {
        const { [this.isDepartmentLeaderboard ? 'departmentPrizes' : 'userPrizes']: scores } = this.leaderboard;
        return scores && Array.isArray(scores.edges) ? scores.edges : null;
      } else {
        return null;
      }
    },
    raffleMode() {
      const { raffleMode } = this.leaderboard || {};
      return raffleMode === true;
    },
    scoresHistory() {
      const { scoresHistory } = this.leaderboard || {};
      return Array.isArray(scoresHistory) ? scoresHistory : [];
    },
  },
  methods: {
    startAutoUpdates() {
      this.updateInterval = setInterval(() => this.$apollo.queries.leaderboard.refresh(), ms('20s'));

      this.historyFrom = new Date();
      this.historyLatest = new Date();

      this.$notification({
        title: 'Auto-refresh enabled',
        message: 'The leaderboard will automatically update',
        variant: 'success',
      });
    },
    stopAutoUpdates() {
      this.updateInterval && clearInterval(this.updateInterval);
      this.updateInterval = null;

      this.historyFrom = null;
      this.historyLatest = null;

      this.$notification({
        title: 'Auto-refresh disabled',
        message: 'The leaderboard will no longer automatically update',
        variant: 'success',
      });
    },
    notifyLatestHistory() {
      const { historyFrom, historyLatest } = this;
      const { scoresHistory: history } = this.leaderboard || {};
      if (historyFrom && historyLatest && Array.isArray(history) && history.length) {
        const DEFAULT_NOTIFICATION_OPTS = {
          appendToast: true,
          noHoverPause: true,
        };

        const latest = history.reduce((list, row) => {
          if (row && row.user && row.user.id && new Date(row.createdAt) > historyLatest) {
            try {
              const notify = createNotificationForScoreHistory(row);
              if (notify && !list.find(r => notify.id === r.id)) {
                list.push({ ...notify, ...DEFAULT_NOTIFICATION_OPTS });
              }
            } catch (err) {
              this.$log.error(err);
            }
          }
          return list;
        }, []);

        this.historyLatest = new Date();

        this.$log.debug(latest.length, JSON.stringify(latest));
        latest.forEach(notify => this.$notification(notify));
      }
    }
  },
  beforeDestroy() {
    this.updateInterval && clearInterval(this.updateInterval);
  },
};

function createNotificationForScoreHistory(history) {
  const { id: eventID, user: { id: userID, fullName }, event: eventName, createdAt } = history;
  const timeago = timeDistance(createdAt, { addSuffix: true });

  const emojis = [
    '🚀', '🎸', '⚡️', '🏆', '🚴‍♀️', '👾', '👻', '🤖', '👔', '💼', '🎒',
    '☀️', '🌤', '⛅️', '🥤', '☕️', '🧊', '⚽️', '🏀', '⚾️', '🥎', '🎱',
    '⛳️', '🏅', '🎫', '🎟', '🎲', '🎯', '🎳', '🎮', '🧩',
  ];

  switch (eventName) {
    case 'JOB_SHARED': return {
      id: `${userID}-${eventName}`,
      title: emojis[Math.floor(Math.random() * emojis.length)],
      message: `${fullName} shared a job ${timeago}`,
      variant: 'info',
    };
    case 'REFERRAL_SUGGESTED': return {
      id: `${userID}-${eventName}`,
      title: emojis[Math.floor(Math.random() * emojis.length)],
      message: `${fullName} sent a job to a referral ${timeago}`,
      variant: 'info',
    };
    case 'APPLICATION_CREATED': return {
      id: eventID,
      title: '🎉',
      message: `${fullName} received a new job application ${timeago}`,
      variant: 'info',
    };
    case 'APPLICATION_INTERVIEW': return {
      id: eventID,
      title: '🎉',
      message: `A referral from ${fullName} moved to interview ${timeago}`,
      variant: 'info',
    };
    case 'APPLICATION_HIRED': return {
      id: eventID,
      title: '🎉',
      message: `A referral from ${fullName} was hired ${timeago}`,
      variant: 'info',
    };
  }
}
</script>

<style lang="scss">
.page.leaderboard-show {

  table {
    th {
      border: none;
      color: rgba(49, 55, 67, 0.5);
    }

    td {
      vertical-align: middle;
      .svg-inline--fa {
        height: 30px;
        width: 30px;
      }
    }

    .flip-list-move {
      transition: transform 1s;
    }
  }

  .opts-icon {
    button {
      padding: 0;
    }

    .svg-inline--fa {
      color: #000;
    }
  }

  .top-header {
    .match-label {
      width: inherit;
    }
  }
  .btn {
    margin-left: 1em;
    min-width: 7em;
    min-height: 3em;
    &.no-background:not(:hover) {
      background: none;
    }
  }
  .points-system {
    min-height: 325px;
    .point-type:nth-child(2) {
      color:  rgba(70, 75, 255, 1);
      background-color:  rgba(70, 75, 255, 0.05);
      .round-icon {
        color:  rgba(70, 75, 255, 1);
        background-color:  rgba(70, 75, 255, 0.15);
      }
    }
    .point-type:nth-child(3) {
      color:  rgba(221, 23, 177, 1);
      background-color:  rgba(221, 23, 177, 0.05);
      .round-icon {
        color:  rgba(221, 23, 177, 1);
        background-color:  rgba(221, 23, 177, 0.15);
      }
    }
    .point-type:nth-child(4) {
      background-color:  rgba(229, 52, 59, 0.05);
      color:  rgba(229, 52, 59,1);
      .round-icon {
        background-color:  rgba(229, 52, 59, 0.15);
        color:  rgba(229, 52, 59,1);
      }
    }
    .point-type:nth-child(5) {
      background-color:  rgba(0, 131, 255, 0.05);
      color:  rgba(0, 131, 255, 1);
      .round-icon {
        background-color:  rgba(0, 131, 255, 0.15);
        color:  rgba(0, 131, 255, 1);
      }
    }
    .point-type:nth-child(6) {
      background-color:  rgba(4, 143, 104, 0.05);
      color:  rgba(4, 143, 104, 1);
      .round-icon {
        background-color:  rgba(4, 143, 104, 0.15);
        color:  rgba(4, 143, 104, 1);
      }
    }
    .point-type {
      padding: 0.5em;
      margin: 1em 0.5em 1em 0.5em;
      .header {
        font-weight: bold;
      }
      .header, span {
        padding-left: 2px;
        display: block;
      }
      .icon-container {
        display: flex;
        align-items: center;
        .round-icon {
          height: 30px;
          width: 30px;
          margin-left : 10px;
          .svg-inline--fa {
            height: 20px;
            width: 20px;
            margin: 5px;
          }
        }
      }
    }

    p {
      margin-top: 2em;
      text-align: center;
    }
    h2 {
      text-align: center;
      margin-bottom: 1em;
    }
  }
  .position {
    h4 {
      font-size: 1.2em;
      text-transform: uppercase;
      font-weight: bolder;
      color: rgba(49, 55, 67, 0.5);
    }
  }
  .prizes {
    text-align: center;

    .percentage-change {
      .svg-inline--fa {
        margin-left: 8.5px;
      }
    }
    .places {
      margin-top: 1em;
      margin-bottom: 1em;
    }
    .prize-item {
      border: none;
      min-height: 300px;
      img {
        margin: 0 auto;
        display: block;
        // max-width: 160px;
        // max-height: 100px;
        margin-bottom: 1em;
      }
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/views/User/LeaderboardShow.vue